import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getAppVersion,
  getBrowserName,
  getDevicePlatform,
  getIPAddress,
  getOS,
  getTimeZone,
} from "@utils/DeviceInfoUtils";
import {
  displayErrorMessage,
  displaySuccessMessage,
} from "@utils/DisplayMessageUtility";
import { FormHelper } from "@utils/FormHelper";
import { getLanguage } from "@utils/LanguageUtils";
import { handleAuthFlow } from "@utils/UserUtils";
import Config from '@config/index';
import { EnvTypes } from "@constants/config";
import { TagManagerService } from "@webServices/TagManagerService";


import {
  ICheckUserExist,
  ILoginPayload,
  IResendVerifyMail,
  ISocialMediaLogin,
  IUserExists,
  IResendOtpPayload
} from "@redux/auth/interface";

import { SignIn } from "@assets/images";
import { translate } from "@translations/Translater";
import { usePrevious, useQuery } from "@utils/CustomHooks";

import PrimaryButton from "@components/atoms/Button/PrimaryButton";
import { AnimatedInput } from "@components/atoms/Input/AnimatedInput";
import Text, { FontFamilyVariant, TextType } from "@components/Text";

import Navigator from "@navigator/index";
import { NavigationUrl, URLs } from "@navigator/NavigationUrl";

import { AUTH_FLOW, platform } from "@constants/config";

import { authMessages } from "@containers/Auth/message";

import AuthForm from "@presentation/auth/components/AuthForm";
import GoogleAuthButton from '@presentation/auth/components/GoogleAuthButton';
import ORDivider from "@presentation/auth/components/ORDivider";
import PasswordAndTerms from "@presentation/auth/components/PasswordForm";
import { TitleAndDescription } from "@presentation/auth/components/TitleAndDescription";
import { LoginModel } from "@common/domain/models/Login";
import { ErrorParser } from "@utils/ErrorMessageUtils";
import { resendOTP } from "@common/redux/batch/actions";

import { AnalyticsService, SCREEN_NAME } from "@webServices/AnalyticsService";
import "./styles.less";
import LegalLinks from "../components/LegalLinks";

interface IProps {
  checkUserExistData: IUserExists;
  checkUserExists: (payload: ICheckUserExist) => void;
  isCheckingUserExists: boolean;
  checkUserExistError: string;

  isRegisteringUser: boolean;
  registerUserError: string;
  registerUserData: any;

  registerUser: (payload: ILoginPayload) => void;

  resendVerifyMailData: IResendVerifyMail;
  resendVerifyMailLoader: boolean;
  resendVerifyMailError: string;
  resendVerifyMail: (payload: ILoginPayload) => void;

  setAuthenticatedFlag: (flag: boolean) => void;
  socialMediaLogin: (payload: ISocialMediaLogin) => void;
  loginLoader: boolean;
  login: (payload: ILoginPayload) => void;
  loginData?: LoginModel;
  loginError: string;
  resendOTP: (payload: IResendOtpPayload) => void;
  resendOtpLoader: boolean;
  resendOTPData: boolean;
  resendOtpError: string;
}

function SelfRegister(props: IProps) {
  const {
    checkUserExistData,
    checkUserExists,
    isCheckingUserExists,
    checkUserExistError,

    isRegisteringUser,
    registerUserError,
    registerUserData,
    registerUser,

    resendVerifyMailData,
    resendVerifyMailLoader,
    resendVerifyMailError,
    resendVerifyMail,
    setAuthenticatedFlag,
    loginLoader,
    login, 
    loginError, 
    loginData,
    resendOTP,
    resendOtpLoader,
    resendOTPData,
    resendOtpError
  } = props;

  const { t } = useTranslation();
  const query = useQuery();
  const instituteCode = query.get("instituteCode");
  const courseKey = query.get("courseKey");
  const featureKey = query.get("featureKey");
  const [isUserExistChecked, setIsUserExistChecked] = useState(false);
  const [isUserExist, setIsUserExist] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [otpText, setOtpText] = useState(translate(t, authMessages.otpSent));

  const [email, setEmail] = useState(null);
  const [emailError, setEmailError] = useState("");

  const [otp, setOtp] = useState(null);

  const [loginErrorMsg, setLoginErrorMsg] = useState<string>('');

  const prevLoginLoader = usePrevious({ loginLoader });

  const prevResendOtpLoader = usePrevious({ resendOtpLoader });

  const [counter, setCounter] = useState(0);

  useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  const [
    isRegisterUserRequestActive,
    setIsRegisterUserRequestActive,
  ] = useState(false);
  const [
    isVerificationEmailRequestActive,
    setIsVerificationEmailRequestActive,
  ] = useState(false);

  const [ipAddress, setIpAddress] = useState("");

  // Effects
  useEffect(() => {
    if (prevLoginLoader && !loginLoader && loginError) {
      setLoginErrorMsg(ErrorParser(loginError));
    }
  }, [loginError, loginLoader]);

  useEffect(() => {
    if (prevResendOtpLoader && !resendOtpLoader && !resendOtpError) {
      setCounter(60);
      setOtpText(translate(t, authMessages.resendOtpSent))
    }
  }, [resendOtpLoader, resendOtpError]);

  useEffect(() => {
    getIPAddress().then((ip) => {
      setIpAddress(ip);
    });
    AnalyticsService.logEvent(AnalyticsService.getPageViewEvent(), { screen_name: SCREEN_NAME.REGISTER });
  }, []);

  useEffect(() => {
    if (!loginLoader) {
      handleAuthFlow();
    }
  }, [loginLoader]);

  const onEmailChange = (e: any) => {
    const { value } = e.target;
    setEmail(value);
    setIsUserExist(false);
    setIsUserExistChecked(false);
    setShowEmailVerification(false);
    setLoginErrorMsg('');
    setCounter(0);
    setOtpText(translate(t, authMessages.otpSent));
    const isValidEmail = !value || !FormHelper.validateEmail(value);
    setEmailError(isValidEmail ? translate(t, authMessages.emailError) : "");
  };

  const onOtpChange = (e: any) => {
    setLoginErrorMsg('');
    const { value } = e.target;
    setOtp(value);
  };

  useEffect(() => {
    if (
      isRegisterUserRequestActive &&
      !isRegisteringUser &&
      !registerUserError
    ) {
      displaySuccessMessage(translate(t, authMessages.otpSent));
      setShowEmailVerification(true);
      setIsRegisterUserRequestActive(false);
      setAuthenticatedFlag(false);
      setCounter(60);
    }
    if (
      isRegisterUserRequestActive &&
      !isRegisteringUser &&
      registerUserError
    ) {
      displayErrorMessage(t("global.errors.TRY_AGAIN"));
      setIsRegisterUserRequestActive(false);
    }
  }, [isRegisteringUser, registerUserError]);

  useEffect(() => {
    if (
      !checkUserExistError &&
      checkUserExistData &&
      checkUserExistData.userExists &&
      !isUserExistChecked
    ) {
      setIsUserExist(true);
    }

    if(!checkUserExistError &&
      checkUserExistData &&
      !checkUserExistData.userExists &&
      !isUserExistChecked && email){
        registerUserHandler();
      }

    if (!isCheckingUserExists && checkUserExistData) {
      setIsUserExistChecked(true);
    }
  }, [checkUserExistData, checkUserExistError]);

  const checkUserExistsHandler = (event) => {
    event.preventDefault();
    checkUserExists({
      userName: email,
    });
    setIsUserExistChecked(false);
    setIsUserExist(false);
    setShowEmailVerification(false);

  };

  const registerUserHandler = () => {
    const payload: ILoginPayload = {
      userName: email,
      termsAndConditionsAcceptance: {
        appVersion: getAppVersion(),
        os: getOS(),
        browser: getBrowserName(),
        platform: getDevicePlatform(),
        ip: ipAddress,
        timezoneName: getTimeZone(),
      },
      language: getLanguage(),
      ...(instituteCode) && { instituteCode: instituteCode }
    };
    if(Config.GOOGLE_TAG_MANAGER_CONFIG.env === EnvTypes.PROD) {
      TagManagerService.pushEvent('signup',{
        email: email,
      });
    }
    registerUser(payload);
    setIsRegisterUserRequestActive(true);
  };

  const resendOtpHandler = () => {
    if (counter === 0) {
      setLoginErrorMsg('');
      setOtp("");
      const payload: IResendOtpPayload = {
        userName: email,
        language: getLanguage()
      };
      resendOTP(payload);
    }
  }  

  useEffect(() => {
    if (isVerificationEmailRequestActive) {
      if (
        resendVerifyMailData &&
        resendVerifyMailData instanceof Object &&
        resendVerifyMailData.hasOwnProperty("resendLink") &&
        resendVerifyMailData.hasOwnProperty("userVerified")
      ) {
        const { resendLink, userVerified } = resendVerifyMailData;
        if (resendLink && !userVerified) {
          displaySuccessMessage(translate(t, authMessages.resendSuccessAlert));
        } else if (!resendLink && userVerified) {
          displaySuccessMessage(translate(t, authMessages.userVerifySuccess));
          Navigator.push(NavigationUrl.generate(URLs.login));
        }
      } else {
        displayErrorMessage(t("global.errors.TRY_AGAIN"));
      }

      setIsVerificationEmailRequestActive(false);
    }
  }, [resendVerifyMailError, resendVerifyMailData]);

  const navigateToSignIn = () => {
    let url = NavigationUrl.generate(URLs.login);
    if(courseKey){
      url = NavigationUrl.generate(URLs.login,{courseKey})
    } else if(featureKey) {
      url = NavigationUrl.generate(URLs.login,{featureKey})
    }
    Navigator.push(url);
  };

  const handleFormSubmit = (e): void => {
    e.preventDefault();
    if (!email || !otp) {
      return;
    }

    const payload: ILoginPayload = {
      userName: email.trim(),
      password: otp.trim(),
    };

    login(payload);
  };

  return (
    <div className="sign_up__container">
    <div className="sign_up__sub_container">
      <AuthForm image={SignIn}>
        {showEmailVerification && (
          <div className="sign_up__otp_section">
             <Text
                className="sign_up__forgot sign_up__otp"
                fontFamilyVariant={FontFamilyVariant.regular}
                text={otpText}
                textType={TextType.h8}
              />
          </div>
        )} 
        <>
          <form onSubmit={checkUserExistsHandler}>
            <div>
              <TitleAndDescription
                description={""}
                title={translate(t, authMessages.createAccount)}
              />

              {!(isUserExistChecked && !isUserExist) && (
                <div className="sign_up__helper">
                  <Text
                    className="sign_up__dont_have"
                    fontFamilyVariant={FontFamilyVariant.regular}
                    text={translate(t, authMessages.accountAlready)}
                    textType={TextType.h8}
                  />
                  <a
                    className="sign_up__sign_up "
                    onClick={navigateToSignIn}
                  >
                    <Text
                      fontFamilyVariant={FontFamilyVariant.semibold}
                      text={translate(t, authMessages.signInText)}
                      textType={TextType.h8}
                    />
                  </a>
                </div>
              )}

              <AnimatedInput
                className="sign_up__input_first"
                error={emailError}
                id="email"
                label={translate(t, authMessages.email)}
                name={translate(t, authMessages.email)}
                onChange={onEmailChange}
                type="email"
                value={email}
              />

              {isUserExist && (
                <Text
                  className="validation-error sign_up__error"
                  fontFamilyVariant={FontFamilyVariant.light}
                  text={translate(t, authMessages.userExist)}
                  textType={TextType.paragraph1}
                />
              )}
              {(!isUserExistChecked || isUserExist) && (
                <>
                  <PrimaryButton
                    className="auth_cmn__button"
                    htmlType="submit"
                    loading={isCheckingUserExists || isRegisteringUser}
                    type="primary"
                    onClick={checkUserExistsHandler}
                    disabled={!FormHelper.validateEmail(email)}
                  >
                    {translate(t, authMessages.createBtn)}
                  </PrimaryButton>
                  <ORDivider text={translate(t, authMessages.or)} />
                  <GoogleAuthButton flow={AUTH_FLOW.SIGN_UP} />
                </>
              )}
            </div>
          </form>
          </>
        {isUserExistChecked && !isUserExist && (
          <>
          <form onSubmit={handleFormSubmit}>
            <AnimatedInput
                className="sign_up__input_first"
                id="otp"
                label={translate(t, authMessages.otp)}
                name="otp"
                onChange={onOtpChange}
                type="password"
                value={otp}
              />
              {/* Needed in future */}
              {showEmailVerification && (
                <div className="sign_up__otp_section" 
                onClick={resendOtpHandler}
                >
                  <Text
                    className={`sign_up__otp_sent ${counter > 0 && 'sign_up__resend_otp'}`}
                    fontFamilyVariant={FontFamilyVariant.regular}
                    //@ts-ignore
                    text={counter === 0 ? translate(t, authMessages.resendTheOTPText) : translate(t, authMessages.resendOTP, {counter})}
                    textType={TextType.h8}
                  />
                </div>
              )} 
            {registerUserError && (
              <Text
                className="validation-error sign_up__error"
                fontFamilyVariant={FontFamilyVariant.light}
                text={translate(t, authMessages.loginError, {
                  context: registerUserError,
                })}
                textType={TextType.paragraph1}
              />
            )}
            {loginErrorMsg && (
              <Text
                className="validation-error sign_in__error"
                fontFamilyVariant={FontFamilyVariant.light}
                text={translate(t, authMessages.otpError)}
                textType={TextType.paragraph1}
              />
            )}
              <div className="sign_up__legal_links">
                <LegalLinks />
              </div>
              <PrimaryButton
                className="auth_cmn__button"
                htmlType="submit"
                loading={loginLoader}
                type="primary"
                onClick={handleFormSubmit}
                disabled={!otp}
              >
                {translate(t, authMessages.submit)}
              </PrimaryButton>
              <ORDivider text={translate(t, authMessages.or)} />
              <GoogleAuthButton flow={AUTH_FLOW.SIGN_UP} />
            </form>
          </>
        )}
      </AuthForm>
    </div>
  </div>
  );
}

export default SelfRegister;
